import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Layers, Mail, ArrowRight, CalendarClock, TrendingUp, CircleDollarSign, AlertCircle, Calendar as CalendarIcon, UserCircle2, Receipt, LayoutDashboard, Activity, Eye, Users } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { logger } from '@/lib/logger';

const money = (v) => `SAR ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ label, value, sub, icon: Icon, tone = 'default', to, testid }) => {
  const toneClass = {
    default: 'bg-white/5 text-white',
    accent: 'bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)]',
    ok: 'bg-green-500/15 text-green-300',
    warn: 'bg-[var(--pg-admin-accent-soft)] text-[var(--pg-admin-accent)]',
    danger: 'bg-red-500/15 text-red-300',
  }[tone];
  return (
    <Link to={to} data-testid={testid} className="rounded-2xl border border-white/10 bg-[#141414] p-3 sm:p-4 shadow-[0_12px_30px_rgba(0,0,0,0.35)] flex items-center gap-2.5 sm:gap-3 hover:border-[var(--pg-admin-accent-ring)] transition-colors">
      <div className={`inline-flex w-9 h-9 sm:w-11 sm:h-11 items-center justify-center rounded-xl shrink-0 ${toneClass}`}><Icon className="w-4 h-4 sm:w-5 sm:h-5" /></div>
      <div className="min-w-0">
        <div className="font-display text-base sm:text-lg leading-none truncate">{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1 truncate">{label}</div>
        {sub && <div className="text-[10px] text-white/40 mt-0.5 truncate hidden sm:block">{sub}</div>}
      </div>
    </Link>
  );
};

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [rev, setRev] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [o, r, a] = await Promise.all([
          api.get('/admin/overview'),
          api.get('/admin/revenue/summary'),
          api.get('/admin/analytics/summary?range=7d'),
        ]);
        setData(o.data);
        setRev(r.data);
        setAnalytics(a.data);
      } catch (err) { logger.warn("Failed to load overview data:", err); } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight inline-flex items-center gap-2"><LayoutDashboard className="w-7 h-7 text-[var(--pg-admin-accent)]" /> Overview</h1>
          <p className="mt-1 text-sm text-white/60">Your coaching business at a glance.</p>
        </div>
        <Link to="/admin/calendar" className="inline-flex items-center gap-1 text-sm text-[var(--pg-admin-accent)] hover:underline">
          Open calendar <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {loading && <div className="mt-6 text-white/60 text-sm">Loading...</div>}

      {!loading && data && (
        <>
          {/* Revenue KPIs — 2 col mobile, 4 col large */}
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
            <StatCard label="This Week" value={money(rev?.week?.revenue_total)} sub={`${rev?.week?.session_count || 0} sessions`} icon={TrendingUp} tone="accent" to="/admin/calendar" testid="stat-week-revenue" />
            <StatCard label="This Month" value={money(rev?.month?.revenue_total)} sub={`${rev?.month?.session_count || 0} sessions`} icon={CircleDollarSign} to="/admin/calendar" testid="stat-month-revenue" />
            <StatCard label="Outstanding" value={money(rev?.outstanding_all_time)} sub="unpaid invoices" icon={AlertCircle} tone="danger" to="/admin/invoices" testid="stat-outstanding" />
            <StatCard label="Today's" value={data.today_sessions || 0} sub={`${rev?.upcoming_next_7_days || 0} next 7 days`} icon={CalendarIcon} to="/admin/calendar" testid="stat-today-sessions" />
          </div>

          {/* Ops KPIs */}
          <div className="mt-2.5 sm:mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
            <StatCard label="Unpaid" value={data.unpaid_invoices || 0} sub={`of ${data.total_invoices || 0} invoices`} icon={Receipt} tone="warn" to="/admin/invoices" testid="stat-unpaid-invoices" />
            <StatCard label="Pending" value={data.pending_bookings || 0} sub="booking requests" icon={CalendarClock} to="/admin/bookings" testid="stat-pending-bookings" />
            <StatCard label="Unread" value={data.unread_messages || 0} sub="messages" icon={Mail} to="/admin/messages" testid="stat-unread-messages" />
            <StatCard label="Clients" value={data.total_clients || 0} icon={UserCircle2} to="/admin/clients" testid="stat-clients" />
          </div>

          {/* Analytics mini row (last 7 days) */}
          <div className="mt-4 rounded-2xl border border-white/10 bg-[#141414] overflow-hidden">
            <header className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold inline-flex items-center gap-2"><Activity className="w-3.5 h-3.5 text-[var(--pg-admin-accent)]" /> Traffic · last 7 days</div>
              <Link to="/admin/analytics" className="text-xs text-[var(--pg-admin-accent)] hover:underline inline-flex items-center gap-1">Open analytics <ArrowRight className="w-3.5 h-3.5" /></Link>
            </header>
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
              <MiniStat icon={Eye} label="Page views" value={Number(analytics?.total_views || 0).toLocaleString()} />
              <MiniStat icon={Users} label="Unique visitors" value={Number(analytics?.unique_visitors || 0).toLocaleString()} />
              <MiniStat icon={TrendingUp} label="Booking conv." value={`${Number(analytics?.booking?.conversion || 0).toFixed(1)}%`} accentValue />
              <MiniStat icon={Mail} label="Contact conv." value={`${Number(analytics?.contact?.conversion || 0).toFixed(1)}%`} />
            </div>
          </div>

          <div className="mt-6 grid lg:grid-cols-2 gap-4">
            <Section title="Today's Sessions" seeAll="/admin/calendar" empty="No sessions scheduled today.">
              {(rev?.today_sessions || []).map((s) => (
                <li key={s.id} className="px-5 py-3 flex items-center gap-3 hover:bg-white/[0.02]">
                  <div className="font-display text-lg text-[var(--pg-admin-accent)] w-16 shrink-0">{format(parseISO(s.start_time), 'HH:mm')}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{s.session_name || s.session_type}</div>
                    <div className="text-xs text-white/50 truncate">{(s.client_names || [s.client_name]).filter(Boolean).join(', ') || '\u2014'}{s.location ? ` \u00b7 ${s.location}` : ''}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold">{money(s.total_price ?? s.price)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50">{s.status}</div>
                  </div>
                </li>
              ))}
            </Section>

            <Section title="Recent Bookings" seeAll="/admin/bookings" empty="No bookings yet.">
              {(data.recent_bookings || []).map((b) => (
                <li key={b.id} className="px-5 py-3 flex items-start justify-between gap-4 hover:bg-white/[0.02]">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="font-semibold text-sm truncate">{b.name}</span><span className="text-xs text-white/50 truncate">· {b.service_title}</span></div>
                    <p className="mt-0.5 text-xs text-white/60 truncate">{b.email}{b.preferred_date ? ` \u00b7 ${b.preferred_date}` : ''}</p>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--pg-admin-accent)]">{b.status}</div>
                </li>
              ))}
            </Section>
          </div>

          {/* Bottom-of-page "other" tile */}
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <StatCard label="Active Services" value={`${data.active_services}/${data.total_services}`} icon={Layers} to="/admin/services" testid="stat-services" />
          </div>
        </>
      )}
    </div>
  );
}

const Section = ({ title, children, seeAll, empty }) => {
  const isEmpty = !children || (Array.isArray(children) && children.filter(Boolean).length === 0);
  return (
    <section className="rounded-2xl border border-white/10 bg-[#141414] overflow-hidden">
      <header className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <h2 className="font-semibold">{title}</h2>
        {seeAll && <Link to={seeAll} className="text-xs text-white/60 hover:text-white">See all →</Link>}
      </header>
      {isEmpty ? <div className="p-6 text-sm text-white/60">{empty}</div> : <ul className="divide-y divide-white/5">{children}</ul>}
    </section>
  );
};


const MiniStat = ({ icon: Icon, label, value, accentValue = false }) => (
  <div className="p-4 sm:p-5 flex items-center gap-3">
    <div className={`inline-flex w-9 h-9 items-center justify-center rounded-xl shrink-0 ${accentValue ? 'bg-[var(--pg-admin-accent-soft)] text-[var(--pg-admin-accent)]' : 'bg-white/5 text-white/85'}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="min-w-0">
      <div className={`font-display text-lg leading-none truncate ${accentValue ? 'text-[var(--pg-admin-accent)]' : ''}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1 truncate">{label}</div>
    </div>
  </div>
);
